import { useCallback } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { DEFAULT_VALUES } from 'src/apps/dashboard/constants/BusinessUnitsTable';
import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { getBusinessUnitsTableData } from 'src/apps/dashboard/services/business-units-table/BusinessUnitsTableService';
import { useBusinessUnitsFromConfig } from 'src/apps/dashboard/stores/dashboardConfigStore';
import { IBusinessUnitRow } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { DASHBOARD_BUSINESS_UNITS_TABLE } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';

interface IUseBusinessUnitsTableDataParams {
	filters?: IFilterOptions;
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard;
}

interface IBusinessUnitsTableQueryResult {
	rows: IBusinessUnitRow[];
	total: number;
	unfilteredTotal: number;
}

export const useBusinessUnitsTableData = ({
	filters,
	labelsExcludedFromDashboard
}: IUseBusinessUnitsTableDataParams): [
	UseQueryResult<IBusinessUnitsTableQueryResult>,
	() => void
] => {
	const { businessUnits, isLoading: isConfigLoading } = useBusinessUnitsFromConfig();
	const { configuration } = useConfiguration();
	const { coreLabels } = processPlatformConfiguration(configuration?.PLATFORM);
	const computedLabelsExcludedFromDashboard =
		labelsExcludedFromDashboard ?? buildLabelsExcludedFromDashboard(coreLabels);

	const selectedPeriod = filters?.dateRange?.[0]?.value
		? Number(filters.dateRange[0].value)
		: DEFAULT_VALUES.SELECTED_PERIOD;

	const queryResult = useQuery(
		[
			DASHBOARD_BUSINESS_UNITS_TABLE,
			selectedPeriod,
			filters,
			businessUnits.length,
			computedLabelsExcludedFromDashboard
		],
		async () =>
			getBusinessUnitsTableData({
				days: selectedPeriod,
				businessUnits,
				filters,
				labelsExcludedFromDashboard: computedLabelsExcludedFromDashboard
			}),
		{
			enabled: !isConfigLoading && businessUnits.length > 0,
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL
		}
	);

	const invalidate = useCallback(
		() =>
			queryClient.invalidateQueries({
				queryKey: [DASHBOARD_BUSINESS_UNITS_TABLE],
				refetchType: 'none'
			}),
		[]
	);

	return [queryResult, invalidate];
};
